import type { Bet, Game, LiveWin, Transfer } from "@/lib/data";
import { currencyLabel, formatAmount, formatMultiplier, multiplier, toBaseUnits, withCurrency } from "@/lib/format/amount";

/* Turns public-API models into the shapes the existing components already
   render, so real data flows through the same tiles and feeds as the mock
   content did. */

type ApiStudio = { id: string; name: string; slug: string; code: string; logoUrl?: string };

type ApiGame = {
  id: string;
  slug: string;
  name: string;
  thumbnailUrl?: string | null;
  isBlocked: boolean;
  isFunModeSupported: boolean;
  studio: ApiStudio;
};

type ApiBetGame = {
  id: string;
  slug: string;
  name: string;
  thumbnailUrl?: string | null;
  studio: ApiStudio;
};

type ApiToken = {
  symbol: string;
  decimals: number;
  significantDecimals: number;
  network?: { environment: string; attributes?: { chainId?: number } | Record<string, unknown> };
};

type ApiTransfer = {
  id: string;
  account: { id?: string; nickname: string; isHidden: boolean };
  token: ApiToken;
  type: string;
  status: string;
  amount: string;
  name?: string | null;
  hash?: string | null;
  createdAt: string;
};

type ApiBet = {
  id: string;
  debit: string;
  credit: string;
  createdAt: string;
  account: { id?: string; nickname: string; isHidden: boolean };
  token: ApiToken;
  game?: ApiBetGame | null;
  hashes?: string[] | null;
};

/* A stable colour per game, so the tile still has the kit's poster rhythm
   while a thumbnail loads and if one is missing entirely. */
const hueFor = (seed: string) => {
  let hash = 0;

  for (const character of seed) {
    hash = (hash * 31 + character.charCodeAt(0)) % 360;
  }

  return hash;
};

export const toGame = (game: ApiGame | ApiBetGame): Game => {
  const hue = hueFor(game.id);

  return {
    id: game.id,
    slug: game.slug,
    title: game.name,
    studio: game.studio.name,
    /* The public API exposes no RTP, so the badge stays empty rather than
       showing a number we invented. */
    rtp: "",
    thumbnailUrl: game.thumbnailUrl ?? undefined,
    from: `hsl(${hue} 72% 48%)`,
    to: `hsl(${(hue + 38) % 360} 78% 22%)`,
    locked: "isBlocked" in game ? game.isBlocked : undefined,
    cats: [],
  };
};

const toLiveWin = (bet: ApiBet): LiveWin | null => {
  if (!bet.game) {
    return null;
  }

  if (toBaseUnits(bet.credit) <= toBaseUnits(bet.debit)) {
    return null;
  }

  const payout = multiplier(bet.debit, bet.credit);

  return {
    game: toGame(bet.game),
    player: bet.account.isHidden ? "Hidden" : bet.account.nickname,
    amount: withCurrency(formatAmount(bet.credit, bet.token), bet.token.symbol),
    multiplier: formatMultiplier(bet.debit, bet.credit),
    big: payout >= 50,
  };
};

export const relativeTime = (iso: string, now: number) => {
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m ago`;
  }

  if (seconds < 86400) {
    return `${Math.round(seconds / 3600)}h ago`;
  }

  return `${Math.round(seconds / 86400)}d ago`;
};

const shortHash = (hash: string | null | undefined) =>
  hash ? `${hash.slice(0, 6)}…${hash.slice(-4)}` : "—";

const toBet = (bet: ApiBet, now: number): Bet | null => {
  if (!bet.game) {
    return null;
  }

  return {
    game: toGame(bet.game),
    user: bet.account.isHidden ? "Hidden" : bet.account.nickname,
    accountId: bet.account.id,
    time: relativeTime(bet.createdAt, now),
    bet: withCurrency(formatAmount(bet.debit, bet.token), bet.token.symbol),
    multiplier: formatMultiplier(bet.debit, bet.credit),
    payout: withCurrency(formatAmount(bet.credit, bet.token), bet.token.symbol),
    ratio: multiplier(bet.debit, bet.credit),
    /* `credit > 0`, the same test the whitelabel's `isWin` makes. A settled
       loss credits nothing, and its payout must not read as a win. */
    credited: toBaseUnits(bet.credit) > 0n,
    hash: shortHash(bet.hashes?.at(0)),
    hashes: bet.hashes ?? undefined,
    chainId:
      bet.token.network?.environment === "evm"
        ? (bet.token.network.attributes as { chainId?: number } | undefined)?.chainId
        : undefined,
  };
};

const TRANSFER_LABEL: Record<string, string> = { credit: "Credit", debit: "Debit" };

export const toTransfers = (transfers: ApiTransfer[], now: number): Transfer[] =>
  transfers.map((transfer) => ({
    id: transfer.id,
    user: transfer.account.isHidden ? "Hidden" : transfer.account.nickname,
    accountId: transfer.account.id,
    time: relativeTime(transfer.createdAt, now),
    kind: transfer.name ?? TRANSFER_LABEL[transfer.type] ?? transfer.type,
    status: transfer.status,
    amount: withCurrency(formatAmount(transfer.amount, transfer.token), transfer.token.symbol),
    incoming: transfer.type !== "debit",
    hash: shortHash(transfer.hash),
    fullHash: transfer.hash ?? undefined,
    /* Without this the VERIFY cell has a hash but no chain to resolve it on, so
       explorerTxUrl returns null and the link collapses to a dash — which is
       why the eye was missing on Live Transactions and Live Rewards. The token
       carries the network the transfer settled on; mirror the bet mapper. */
    chainId:
      transfer.token.network?.environment === "evm"
        ? (transfer.token.network.attributes as { chainId?: number } | undefined)?.chainId
        : undefined,
  }));

/* Big-win cards: the highest-paying settled rounds, biggest first. */
export const toBigWins = (bets: ApiBet[], take = 8) =>
  bets
    .filter(
      (bet) =>
        bet.game !== null &&
        bet.game !== undefined &&
        toBaseUnits(bet.credit) > toBaseUnits(bet.debit),
    )
    .map((bet) => ({
      id: bet.id,
      game: toGame(bet.game as ApiBetGame),
      payout: withCurrency(formatAmount(bet.credit, bet.token), bet.token.symbol),
      multiplier: formatMultiplier(bet.debit, bet.credit),
      bet: withCurrency(formatAmount(bet.debit, bet.token), bet.token.symbol),
      player: bet.account.isHidden ? "Hidden" : bet.account.nickname,
      hash: shortHash(bet.hashes?.at(0)),
      ratio: multiplier(bet.debit, bet.credit),
    }))
    .sort((left, right) => right.ratio - left.ratio)
    .slice(0, take);

export type BigWin = ReturnType<typeof toBigWins>[number];

type ApiBankroll = {
  id: string;
  token: ApiToken & { logoUrl: string; address: string };
  address: string;
  name: string;
};

/* Bankroll tiles. The API states the pool's address and its token; the balance
   is on chain — the ERC-20 balanceOf that address — and is read separately (see
   chain-balance.ts), the same figure the whitelabel shows. The tile carries
   everything that read needs plus the token's own scale for formatting it. */
export const toBankrollTiles = (bankrolls: ApiBankroll[]) =>
  bankrolls.map((bankroll) => ({
    id: bankroll.id,
    /* The platform names a bankroll after its token — "USDSM House Bankroll" —
       and the bankroll surfaces show that token's own ticker, so the name
       stays as the platform wrote it. */
    label: bankroll.name,
    symbol: bankroll.token.symbol.toUpperCase(),
    address: bankroll.address,
    tokenAddress: bankroll.token.address,
    tokenLogoUrl: bankroll.token.logoUrl,
    decimals: bankroll.token.decimals,
    significantDecimals: bankroll.token.significantDecimals,
    chainId:
      bankroll.token.network?.environment === "evm"
        ? (bankroll.token.network.attributes as { chainId?: number } | undefined)?.chainId
        : undefined,
    /* The on-chain balance, in base units. Filled by the server read; the tile
       shows the symbol alone until it arrives. */
    balance: null as string | null,
  }));

export type BankrollTile = ReturnType<typeof toBankrollTiles>[number];

export const toGames = (games: ApiGame[]) => games.map(toGame);

export const toLiveWins = (bets: ApiBet[]) =>
  bets.map(toLiveWin).filter((win): win is LiveWin => win !== null);

export const toBets = (bets: ApiBet[], now: number) =>
  bets.map((bet) => toBet(bet, now)).filter((entry): entry is Bet => entry !== null);

/* ------------------------------------------------------------- sportsbook */

/* A sportsbook bet is a betslip: one stake against one or more legs, each leg
   a selection in a market on a fixture. The number of legs is what the UI
   names it by — `betType` is the provider's own system-bet notation and is
   left alone rather than guessed at. */

type ApiSportsbookLeg = {
  id: string;
  sport: string | null;
  category: string | null;
  tournament: string | null;
  competitors: string[] | null;
  market: string | null;
  selection: string | null;
  odds: string | null;
  isLive: boolean | null;
};

type ApiSportsbookBet = {
  id: string;
  debit: string;
  credit: string;
  createdAt: string;
  closedAt: string | null;
  account: { id?: string; nickname: string; isHidden: boolean };
  token: ApiToken;
  betslip: {
    totalOdds: number | null;
    potentialWin: string | null;
    status: string;
    bets: ApiSportsbookLeg[];
  };
};

/* Whether a slip is finished is read from `closedAt`, not from its status.
   The status vocabulary belongs to the provider and is open — the dev API
   alone returns won, lost, refund, cashed_out, half_won and half_lost — so any
   list of "these mean settled" is a list that goes stale. `settledAt` is not
   the answer either: it is null on plenty of slips that have already paid.

   For the same reason the tone comes from the money rather than the label.
   More back than staked is a good result whatever the provider called it, and
   a status we have never seen still renders correctly. */
const outcomeOf = (debit: string, credit: string) => {
  if (!/^\d+$/.test(debit) || !/^\d+$/.test(credit)) {
    return "even" as const;
  }

  const staked = BigInt(debit);
  const returned = BigInt(credit);

  if (returned > staked) {
    return "up" as const;
  }

  return returned < staked ? ("down" as const) : ("even" as const);
};

/* `cashed_out` is the provider's spelling, not something to show a player. */
const statusLabel = (status: string) => status.replace(/_/g, " ");

const returnedAmount = (amount: string | null, token: ApiToken) =>
  amount === null ? "—" : withCurrency(formatAmount(amount, token), token.symbol);

const toLeg = (leg: ApiSportsbookLeg) => ({
  id: leg.id,
  sport: leg.sport ?? "",
  tournament: [leg.category, leg.tournament].filter(Boolean).join(" · "),
  /* Two competitors is a fixture and reads as "A v B"; an outright has one. */
  fixture: (leg.competitors ?? []).join(" v "),
  market: leg.market ?? "",
  selection: leg.selection ?? "",
  odds: leg.odds ?? "",
  live: leg.isLive === true,
});

export type SportsbookLeg = ReturnType<typeof toLeg>;

export const toSportsbookBets = (bets: ApiSportsbookBet[], now: number) =>
  bets
    /* A slip with no legs is nothing to show — no fixture, no market, no
       price. Rendering it would be an empty card. */
    .filter((bet) => bet.betslip.bets.length > 0)
    .map((bet) => {
    const { betslip } = bet;
    const legs = betslip.bets.map(toLeg);
    const settled = bet.closedAt !== null;

    return {
      id: bet.id,
      player: bet.account.isHidden ? "Hidden" : bet.account.nickname,
      accountId: bet.account.id,
      time: relativeTime(bet.createdAt, now),
      stake: withCurrency(formatAmount(bet.debit, bet.token), bet.token.symbol),
      /* While a slip is open the payout has not been decided, so it shows what
         it stands to return rather than the zero its credit currently holds. */
      returns: returnedAmount(settled ? bet.credit : betslip.potentialWin, bet.token),
      settled,
      status: statusLabel(betslip.status),
      outcome: settled ? outcomeOf(bet.debit, bet.credit) : ("open" as const),
      odds: betslip.totalOdds === null ? "" : betslip.totalOdds.toFixed(2),
      legCount: legs.length,
      live: legs.some((leg) => leg.live),
      legs,
    };
  });

export type SportsbookBet = ReturnType<typeof toSportsbookBets>[number];

/* The provider and the app do not always agree on what a sport is called —
   BetBy says Soccer where the site says Football — so matching a slip to a
   sport goes through the names each side actually uses. */
const SPORT_ALIASES: Record<string, string> = {
  soccer: "football",
  football: "soccer",
  "ice hockey": "icehockey",
  icehockey: "ice hockey",
};

export const betIsSport = (bet: SportsbookBet, label: string) => {
  const wanted = label.trim().toLowerCase();

  return bet.legs.some((leg) => {
    const sport = leg.sport.trim().toLowerCase();

    return sport === wanted || SPORT_ALIASES[sport] === wanted;
  });
};
